// import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import {
  DecoupledEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  Undo,
  Heading,
  Link,
  List,
  BlockQuote,
  Indent,
  IndentBlock,
  Table,
  AutoLink,              
  TableToolbar,
  TableProperties,
  TableCellProperties,
  GeneralHtmlSupport,
  Underline,
  FontColor,
  TableColumnResize,
  FontBackgroundColor,
  Image as CKEImage,
  ImageUpload as CKEImageUpload,
  ImageToolbar as CKEImageToolbar,
  ImageCaption as CKEImageCaption,
  Mention
} from 'ckeditor5';
import AgendaElementsPlugin from './ck5-agenda-plugin';

export const CK5_VALID_IMAGE_TYPES = ['image/gif','image/jpeg','image/png'];
export const CK5_MAX_UPLOAD_KB = 2048; // 2MB
export const CK5_IMAGE_MAX_WIDTH = 670; // px
export const CK5_TOOLBAR: string[] = [
  'undo','redo','|','bold','italic','underline','|',
  'numberedList','bulletedList','|','insertTable','imageUpload','|',
  'fontColor','fontBackgroundColor','link'
];

// Export a single Editor build to be reused by Notes/Tasks/Decisions
export const CK5_EDITOR = DecoupledEditor;

// Build the standard editable config; accepts meetingResponse to wire @mentions
export function buildCk5Config(meetingResponse: any) {
  return {
    plugins: [
        Bold,
        Essentials,
        Italic,
        Paragraph,
        Undo,
        Heading,
        Link,
        List,
        BlockQuote,
        Indent,
        AutoLink,
        IndentBlock,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        GeneralHtmlSupport,
        AgendaElementsPlugin,
        Underline,
        FontColor,
        FontBackgroundColor,
        TableColumnResize,
        CKEImage,
        CKEImageUpload,
        CKEImageToolbar,
        CKEImageCaption,
        Mention
      ],
    toolbar: { items: CK5_TOOLBAR },
    table: { contentToolbar: ['tableColumn','tableRow','mergeTableCells','tableProperties',
      'tableCellProperties'], resizeEnabled: true
    },
    
    htmlSupport: {
        allow: [
          {
            name: /^.*$/,
            classes: true,
            attributes: true,
            styles: true
          }
        ],
        disallow: []
      },
      htmlEmbed: {
        showPreviews: true
      },
      fontColor: {

        colorPicker: {
  
          format: 'hex'  // Output HEX instead of HSL
  
        }
  
      },
      
  
      fontBackgroundColor: {
  
        colorPicker: {
  
          format: 'hex'  // Output HEX instead of HSL
  
        }
  
      }
   
  } as any;
}

export const CK5_READONLY_CONFIG: any = { toolbar: [], table: { contentToolbar: [] } };


export function resizeImageFileBrowser(file: File, maxWidth = CK5_IMAGE_MAX_WIDTH): Promise<File> {
  return new Promise((resolve) => {
    try {
      if (file.type === 'image/gif') { resolve(file); return; }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const w = (img as HTMLImageElement).naturalWidth;
        const h = (img as HTMLImageElement).naturalHeight;
        const scale = Math.min(1, maxWidth / w);
        if (scale >= 1) { URL.revokeObjectURL(url); resolve(file); return; }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); resolve(file); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const mime = (file.type === 'image/png' || file.type === 'image/jpeg') ? file.type : 'image/jpeg';
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) { resolve(file); return; }
          const resized = new File([blob], file.name, { type: mime });
          resolve(resized);
        }, mime, 0.92);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch { resolve(file); }
  });
}

export function setupCk5UploadAdapter(
  editor: any,
  apiBase: string,
  attachmentBase: string,
  meetingId: any,
  agendaId: any,
  onUploaded?: (url: string) => void
) {
  try {
    const uploadUrl = apiBase + attachmentBase + 'imageUpload';
    const downloadBase = apiBase + attachmentBase + 'imageDownload/';

    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
      return {
        upload: () => loader.file.then((file: File) => new Promise(async (resolve, reject) => {
          const sizeKb = Math.round(file.size / 1024);
          if (!CK5_VALID_IMAGE_TYPES.includes(file.type)) { reject('Unsupported image type'); return; }

          // Resize client-side
          let fileToSend: File = file;
          try { fileToSend = await resizeImageFileBrowser(file, CK5_IMAGE_MAX_WIDTH); } catch {}
          const resizedKb = Math.round(fileToSend.size / 1024);
          if (resizedKb >= CK5_MAX_UPLOAD_KB) { reject('File size exceeds 2MB after resize'); return; }

          const form = new FormData();
          form.append('fileUpload', fileToSend, fileToSend.name);
          const attachmentJson = {
            fkEntityId: agendaId,
            fkMeetingId: meetingId,
            orgCode: localStorage.getItem('orgCode') || (localStorage as any).orgCode
          } as any;
          if (!attachmentJson.fkEntityId || !attachmentJson.fkMeetingId || !attachmentJson.orgCode) {
            reject('Missing required upload fields');
            return;
          }
          form.append('attachment', JSON.stringify(attachmentJson));

          const xhr = new XMLHttpRequest();
          xhr.open('POST', uploadUrl, true);
          const token = localStorage.getItem('token');
          const userName = localStorage.getItem('encLoginName') || '';
          if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
          xhr.setRequestHeader('userName', userName);
          xhr.responseType = 'json';
          xhr.onerror = () => { reject('Image upload failed'); };
          xhr.onload = () => {
            const resp = xhr.response || {};
            if (xhr.status >= 200 && xhr.status < 300 && resp.attachmentId) {
              const url = downloadBase + resp.attachmentId;
              resolve({ default: url });
              try { onUploaded && onUploaded(url); } catch {}
            } else {
              reject(resp?.message || 'Upload failed');
            }
          };
          xhr.send(form);
        })),
        abort: () => { /* noop */ }
      };
    };
  } catch {}
}


